import { isFuture } from 'date-fns'

import {
  GetCards200ResponseInner,
  GetCurrentCardAssociatedWithTheUserAccount200Response,
} from '@src/api/generated/user-api'
import {
  CARD_BG_IMAGE_PATHS,
  CARD_ICON_IMAGE_PATHS,
} from '@src/consts/cards/cards'
import { formatDate } from '@src/libs'

import { CardTypes, IsAvailableArgs, CardIconImagePathArgs } from './type'

const isLastDateOfExpirationDate = (expiredDate?: string) => {
  if (!expiredDate) return true
  const formattedToday = formatDate(new Date(), { dateFormat: 'yyyyMMdd' })
  const formattedExpiredDate = formatDate(new Date(expiredDate), {
    dateFormat: 'yyyyMMdd',
  })

  return formattedToday === formattedExpiredDate
}

export const isExpiryDateWithin = (expiredDate?: string | null) => {
  if (!expiredDate) return true

  if (isLastDateOfExpirationDate(expiredDate)) return true
  return isFuture(new Date(expiredDate))
}

export const cardIconImagePath = ({
  cardCategory,
  cardState,
  ignoreAvailableStatus,
}: CardIconImagePathArgs) => {
  // Metal Card
  if (cardCategory.isMetalCard) {
    if (ignoreAvailableStatus || isAvailable(cardState)) {
      return CARD_ICON_IMAGE_PATHS.METAL_CARD
    }
    return CARD_ICON_IMAGE_PATHS.METAL_CARD_LOCKED
  }

  // Virtual Card
  if (cardCategory.isVirtualCard || cardCategory.isOnDemand) {
    if (ignoreAvailableStatus || isAvailable(cardState)) {
      return CARD_ICON_IMAGE_PATHS.VIRTUAL_CARD
    }
    return CARD_ICON_IMAGE_PATHS.VIRTUAL_CARD_LOCKED
  }

  // Physical Card
  if (ignoreAvailableStatus || isAvailable(cardState)) {
    return CARD_ICON_IMAGE_PATHS.REAL_CARD
  }
  return CARD_ICON_IMAGE_PATHS.REAL_CARD_LOCKED
}

export const combineCardName = (
  cardLabel: string,
  cardHolder: string,
  userAccountGroup: GetCards200ResponseInner['userAccountGroup']
) => {
  if (!userAccountGroup) return `${cardLabel}/${cardHolder}`
  return `${cardLabel}/${cardHolder}/${userAccountGroup.name}`
}

export function cardBgImagePath(
  item: GetCurrentCardAssociatedWithTheUserAccount200Response | undefined
): string {
  if (!item) return CARD_BG_IMAGE_PATHS.REAL_CARD

  // Metal Card
  if (item.isMetalCard) {
    return getCardBgImagePath('METAL_CARD', isAvailable(item))
  }

  // OnDemand Card
  if (item.isOnDemand) {
    return getCardBgImagePath('ON_DEMAND_CARD', isAvailable(item))
  }

  // Virtual Card
  if (item.isVirtualCard) {
    return getCardBgImagePath('VIRTUAL_CARD', isAvailable(item))
  }

  // Physical Card
  return getCardBgImagePath('REAL_CARD', isAvailable(item))
}

export function disabledMessageForBgImage(
  item: GetCurrentCardAssociatedWithTheUserAccount200Response | undefined
): string {
  if (!item) return ''

  if (item.isDeactivated) {
    return 'このカードは解約済です'
  }
  if (item.isFrozen) {
    return 'このカードは凍結されています'
  }
  if (item.isSuspended) {
    return '管理者によって一時ロックされています'
  }
  if (item.isLocked) {
    return 'カード保有者によって一時ロックされています'
  }
  if (!isExpiryDateWithin(item.expiredDate)) {
    return '有効期限切れ'
  }
  return ''
}

function getCardBgImagePath(cardType: CardTypes, isAvailable: boolean): string {
  const key: keyof typeof CARD_BG_IMAGE_PATHS = isAvailable
    ? cardType
    : `${cardType}_LOCKED`
  return CARD_BG_IMAGE_PATHS[key]
}

export const isAvailable = ({
  isActivated,
  isSuspended,
  isLocked,
  isFrozen,
  isDeactivated,
  expiredDate,
}: IsAvailableArgs) => {
  return (
    isActivated &&
    !isSuspended &&
    !isLocked &&
    !isFrozen &&
    !isDeactivated &&
    isExpiryDateWithin(expiredDate)
  )
}

function getCardMap(rawCards: GetCards200ResponseInner[] | undefined) {
  if (!rawCards) return new Map<string, GetCards200ResponseInner>()
  return rawCards.reduce(
    (map, card) =>
      map.has(card.userAccountId) ? map : map.set(card.userAccountId, card),
    new Map<string, GetCards200ResponseInner>()
  )
}

export function getCardOptions(
  rawCards: GetCards200ResponseInner[] | undefined
) {
  if (!rawCards) return []
  return rawCards.map((v) => ({
    label: v.cardLabel,
    value: v.userAccountId,
  }))
}

export function getCardLabel(
  rawCards: GetCards200ResponseInner[] | undefined,
  userAccountId: string
) {
  const cardMap = getCardMap(rawCards)
  return cardMap.get(userAccountId)?.cardLabel || null
}
